'use client';

import { useEffect, useRef } from 'react';
import { useId } from 'react-aria';
import { useCachedValue } from 'components/Collections/hooks/useCachedValue';
import { isMobile } from 'utils/is-mobile';
import { getInteractionModality } from '@react-aria/interactions';
import { useControlledComponent } from '../../hooks/useControlledComponent';
import { getExitSideFromRectangle } from './getExitSideFromRectangle';
import { getHull } from './getHull';
import { getPaddedExitPoints } from './getPaddedExitPoints';
import { getPointsFromRectangle } from './getPointsFromRectangle';
import { isPointInPolygon } from './isPointInPolygon';
import { useTooltipContext } from './TooltipContext';
import type { TargetHoverBindingProps } from './useHoverTarget';

export type TargetHoverProps = {
  ref: any;
} & Partial<TargetHoverBindingProps>;

export type UseTooltipProps = {
  /** Whether or not the Tooltip is showing. */
  isShowing?: boolean;
  /** Handle changing whether the Popover is showing (when used as a controlled component). */
  onRequestChange?: (isShowing: boolean) => void;
  /** Determine if the Popover should appear by default when using as an uncontrolled component. */
  defaultShowing?: boolean;
  /** Delay in ms before a Tooltip appears. */
  delayIn?: number;
  /** Optionally provide your own target element. */
  anchorRef?: any;
  /** Optionally disable the Tooltip. */
  disabled?: boolean;
  /** Optionally enable on mobile devices. */
  enableOnMobile?: boolean;
  /** Optionally specify an id. */
  id?: string;
  /**
   * Optionally prevent the tooltip from describing its target element to a screen reader.
   * This is useful when the target element is already described by another element
   * or by an aria-label, and the tooltip content duplicates this information.
   */
  skipAriaDescription?: boolean;
};

export function useTooltip({
  isShowing: isShowingProp,
  onRequestChange,
  defaultShowing = false,
  delayIn = 100,
  disabled = false,
  enableOnMobile = false,
  id: idProp,
  anchorRef,
  skipAriaDescription = false,
}: UseTooltipProps = {}) {
  let isDisabled = disabled;

  if (!enableOnMobile && isMobile) {
    isDisabled = true;
  }

  // use a unique id for accessibility purposes
  const id = useId(idProp);

  const tooltipRef = useRef<HTMLElement>(null);
  const targetRef = useRef<HTMLElement>(null);
  const isHoverModalityRef = useRef(false);

  const [isShowing, setIsShowing] = useControlledComponent(
    isShowingProp,
    onRequestChange,
    defaultShowing
  );

  if (isDisabled && isShowing) {
    setIsShowing(false);
  }

  const {
    isCoolingDown,
    showingId: currentGlobalId,
    setShowingId: setCurrentGlobalId,
  } = useTooltipContext();

  const mountTimer = useRef<ReturnType<typeof setTimeout>>();

  const controllerRef = useRef<AbortController>();

  useEffect(() => {
    return () => {
      clearTimeout(mountTimer.current);
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  /**
   * If the tooltip was triggered via hover, and another tooltip opens
   * via hover, we want to close this tooltip. This only applies when hovering
   * over buttons with tooltips that render within the grace area.
   */

  useEffect(() => {
    if (isHoverModalityRef.current && id !== currentGlobalId) {
      setIsShowing(false);
    }
  }, [currentGlobalId, id, setIsShowing]);

  /**
   * Upon leaving the target element, we want to draw a shape between
   * the target element and the hover target element. If the mouse leaves this shape,
   * we want to hide the tooltip.
   */

  function onLeaveGraceArea(
    e: React.MouseEvent<any>,
    hoverTarget: HTMLElement,
    onLeave: () => void
  ) {
    const currentTarget = e.currentTarget as HTMLElement;
    const exitPoint = { x: e.clientX, y: e.clientY };
    const exitSide = getExitSideFromRectangle(
      exitPoint,
      currentTarget.getBoundingClientRect()
    );
    const graceArea = getHull([
      ...getPaddedExitPoints(exitPoint, exitSide),
      ...getPointsFromRectangle(hoverTarget.getBoundingClientRect()),
    ]);

    controllerRef.current = new AbortController();

    document.addEventListener(
      'mousemove',
      (e) => {
        const target = e.target as HTMLElement;
        const pointerPosition = { x: e.clientX, y: e.clientY };
        const hasEnteredTarget =
          targetRef.current?.contains(target) ||
          tooltipRef.current?.contains(target);
        const isPointerOutsideGraceArea = !isPointInPolygon(
          pointerPosition,
          graceArea
        );

        if (hasEnteredTarget) {
          controllerRef.current!.abort();
        } else if (isPointerOutsideGraceArea) {
          controllerRef.current!.abort();
          onLeave();
        }
      },
      {
        signal: controllerRef.current.signal,
      }
    );
  }

  const prevShowing = useRef(isShowing);

  /**
   * If the tooltip is showing, we want to set the current global id. This
   * will ensure other open tooltips will close.
   */

  useEffect(() => {
    if (!prevShowing.current && isShowing) {
      setCurrentGlobalId(id);
    }

    prevShowing.current = isShowing;
  });

  function hideTooltip() {
    setIsShowing(false);
    isHoverModalityRef.current = false;
  }

  const cachedCoolingDown = useCachedValue(isCoolingDown);

  const targetInteractionBindings = {
    'aria-describedby': isShowing && !skipAriaDescription ? id : undefined,
    onMouseEnter: () => {
      const show = () => {
        isHoverModalityRef.current = true;
        setIsShowing(true);
      };

      /**
       * In cases where a tooltip has recently been triggered,
       * we want to render the next tooltip immediately without
       * the delay.
       */

      if (cachedCoolingDown.current) {
        show();
        return;
      }

      mountTimer.current = setTimeout(() => {
        show();
      }, delayIn);
    },
    onMouseDown: () => {
      clearTimeout(mountTimer.current);
      hideTooltip();
    },
    onFocus: () => {
      if (getInteractionModality() === 'keyboard') {
        setIsShowing(true);
      }
    },
    onBlur: () => {
      hideTooltip();
    },
    onMouseLeave: (e: React.MouseEvent<any>) => {
      clearTimeout(mountTimer.current);
      if (isShowing) {
        setCurrentGlobalId(null);
        onLeaveGraceArea(e, tooltipRef.current!, hideTooltip);
      }
    },
  };

  let targetProps: {
    ref: any;
  } & Partial<typeof targetInteractionBindings> = {
    ref: targetRef,
  };

  if (!isDisabled) {
    targetProps = {
      ...targetProps,
      ...targetInteractionBindings,
    };
  }

  const contentProps = {
    id,
    ref: tooltipRef,
    target: anchorRef ? anchorRef.current : targetRef.current,
    role: 'tooltip',
    isShowing,
    animateOnMount: !isCoolingDown,
    animateOnUnmount: !(currentGlobalId && currentGlobalId !== id),
    shouldUsePositioner: true,
    onMouseLeave: (e: React.MouseEvent<any>) => {
      onLeaveGraceArea(e, targetRef.current!, hideTooltip);
    },
  };

  return {
    isShowing,
    targetRef,
    bindTarget: targetProps,
    bindContent: contentProps,
  };
}
